import { AppStore, NavDetailTabs } from '@app/models';
import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { StoreProvider, SwiperProvider } from '@app/providers';
import { asapScheduler, observeOn, Subscription } from 'rxjs';
import Swiper from 'swiper';

@Component({
    standalone: false,
    selector: 'app-detail-nav',
    templateUrl: './detail-nav.component.html',
    styleUrls: ['./detail-nav.component.scss'],
})
export class DetailNavComponent implements OnInit, AfterViewInit, OnDestroy {
    public app!: AppStore;
    @Input() items?: NavDetailTabs[];
    @Input() active?: NavDetailTabs;

    // Nom de l'item actif par défaut, configurable selon la page parente
    // (ex: 'main' pour /counting, 'heatmap-static' pour /journey)
    @Input() defaultActiveName = 'main';

    // Si true : nav fixe centrée, sans carrousel infini (utilisé sur /counting)
    @Input() staticMode = false;

    private $appSubscription!: Subscription;
    private swiper: any = null;
    public settings: any = null;

    private settingsSub$!: Subscription;

    @ViewChild('swiper') swiperRef!: ElementRef;

    // Cause swiper is bugged.
    private realIndex = 0;
    private isCreated = false;

    constructor(
        private swiperProvider: SwiperProvider,
        public $store: StoreProvider
    ) {}

    ngOnInit(): void {
        this.settingsSub$ = this.$store.settings$.subscribe((settings) => {
            this.settings = settings;
        });
    }

    ngAfterViewInit(): void {
        // Abonnement créé après le rendu de la vue : appliquer les valeurs au
        // cycle suivant évite NG0100 sur isActive / isItemDisabled.
        this.$appSubscription = this.$store
            .listen('app')
            .pipe(observeOn(asapScheduler))
            .subscribe((app: AppStore) => {
                this.app = app;
                this.realIndex = app!.props!.detailNav!.items!.findIndex(
                    (item) => item.name === this.defaultActiveName
                );

                if (this.staticMode) {
                    // Pas de swiper en mode statique
                    return;
                }

                if (
                    app!.props!.detailNav!.items!.length > 0 &&
                    !this.isCreated
                ) {
                    this.isCreated = true;
                    this.createSwiper();
                    setTimeout(() => {
                        this.resetSwipper();
                        this.createSwiper();
                        this.listenSwiper();
                    });
                }
            });
    }

    // Items filtrés : on masque "entry-passage" si une seule entrée est configurée
    public get visibleItems(): NavDetailTabs[] {
        if (!this.items) return [];

        if (this.settings?.entriesCount === 1) {
            return this.items.filter((item) => item.name !== 'entry-passage');
        }

        return this.items;
    }

    private createSwiper() {
        this.swiper = this.swiperProvider.create(this.swiperRef.nativeElement, {
            direction: 'horizontal',
            pagination: { clickable: true },
            navigation: true,
            slidesPerView: 5,
            spaceBetween: 25,
            normalizeSlideIndex: true,
            loop: true,
            centeredSlides: true,
            allowSlideNext: true,
            allowSlidePrev: true,
            allowTouchMove: true,
            slideToClickedSlide: true,
            initialSlide: this.realIndex,
        });
    }

    private listenSwiper() {
        // Depuis Swiper 9, le recalage de la boucle émet slideChange en double :
        // realIndexChange n'est émis qu'au changement effectif d'onglet.
        this.swiperRef.nativeElement.swiper.on(
            'realIndexChange',
            this.swipperClick.bind(this)
        );
    }

    private swipperLogic(direction: 'prev' | 'next') {
        if (direction === 'prev') {
            this.realIndex = this.realIndex + 1;
        }
        if (direction === 'next') {
            this.realIndex = this.realIndex + 1;
        }
    }

    private sliderLogic(direction: 'prev' | 'next', amount: number) {
        const numberOfTurns =
            Math.floor(amount / this.app!.props!.detailNav!.items!.length) - 1;

        const sign = direction === 'next' ? 1 : -1;

        if (numberOfTurns === 0) {
            this.realIndex = this.realIndex + amount;
        } else {
            const numberOfIndexToSlides =
                (amount % this.app!.props!.detailNav!.items!.length) * sign;

            this.realIndex = this.realIndex + numberOfIndexToSlides;
        }
    }

    private swipperClick(ctx: Swiper) {
        const direction = ctx.realIndex > ctx.previousIndex ? 'next' : 'prev';
        if (
            Math.abs(this.realIndex) >=
            Math.trunc(this.app!.props!.detailNav!.items!.length / 2) + 1
        ) {
            this.realIndex = 0;
        } else {
            this.swipperLogic(direction);
        }
        if (this.items) {
            const selectedItem = this.items[ctx.realIndex];
            this.$store.emit({
                app: {
                    props: {
                        detailNav: {
                            active: selectedItem,
                        },
                    },
                },
            });
        }
    }

    private resetSwipper() {
        if (this.swiper !== null) {
            this.swiper.destroy();
            this.swiper = null;
        }
    }

    // Onglets verrouilles : le magasin ne dispose pas de l'option associee
    // (meme condition que l'encart d'abonnement affiche dans le contenu)
    public isItemDisabled(item: NavDetailTabs): boolean {
        if (item.name !== 'age-gender-recognition') return false;

        return !this.app?.props?.selectedLocalBusiness?.ageGenderEnabled;
    }

    // Sélection directe d'un item en mode statique (pas de swiper)
    public selectStaticItem(item: NavDetailTabs) {
        if (this.isItemDisabled(item)) return;

        this.$store.emit({
            app: {
                props: {
                    detailNav: {
                        active: item,
                    },
                },
            },
        });
    }

    public isActive(item: NavDetailTabs): boolean {
        return this.app?.props?.detailNav?.active?.name === item.name;
    }

    ngOnDestroy(): void {
        this.settingsSub$?.unsubscribe();
        this.$appSubscription?.unsubscribe();

        // app est reçu de façon asynchrone : il peut manquer si le composant
        // est détruit aussitôt créé, et rien n'a alors été sélectionné.
        const detailNavItems = this.app?.props?.detailNav?.items;
        if (this.staticMode || !detailNavItems) {
            return;
        }

        this.realIndex = detailNavItems.findIndex(
            (item) => item.name === this.defaultActiveName
        );
        if (this.items) {
            this.$store.emit({
                app: {
                    props: {
                        detailNav: {
                            active: this.items[this.realIndex],
                        },
                    },
                },
            });
        }
    }
}
